/**
 * Wire codec (protocol.md §2): JSON, UTF-8, camelCase. The envelope types are already camelCase, so
 * encode/decode is JSON with a light structural validation + tolerant-reader behavior (unknown
 * fields on events/responses are preserved and ignored, never rejected).
 */
import type { Envelope } from './envelope.ts';
import { PROTOCOL_VERSION } from './envelope.ts';

/** Serialize an envelope to a wire string. Undefined fields are dropped (matches WhenWritingNull). */
export function encode(envelope: Envelope): string {
  return JSON.stringify(envelope);
}

/**
 * Parse a wire string into an {@link Envelope}. Returns null for malformed JSON or a non-object /
 * missing `type` (a tolerant reader drops garbage rather than throwing into the receive loop).
 */
export function decode(raw: string): Envelope | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const obj = value as Record<string, unknown>;
  if (typeof obj.type !== 'string') {
    return null;
  }
  // `v` defaults to the current protocol version if the peer omitted it (tolerant reader).
  const v = typeof obj.v === 'number' ? obj.v : PROTOCOL_VERSION;
  return {
    type: obj.type,
    v,
    session: typeof obj.session === 'string' ? obj.session : undefined,
    seq: typeof obj.seq === 'number' ? obj.seq : undefined,
    cmdId: typeof obj.cmdId === 'string' ? obj.cmdId : undefined,
    actor: typeof obj.actor === 'string' ? obj.actor : undefined,
    ts: typeof obj.ts === 'string' ? obj.ts : undefined,
    payload: obj.payload ?? {},
  };
}

/** Build a client command envelope (no `seq`/`actor` — those are server-authoritative). */
export function command<P>(type: string, session: string, cmdId: string, payload: P): Envelope<P> {
  return {
    type,
    v: PROTOCOL_VERSION,
    session,
    cmdId,
    payload,
  };
}

/** Build a connection-scoped `sys.*` frame (no session). */
export function control<P>(type: string, payload: P): Envelope<P> {
  return { type, v: PROTOCOL_VERSION, payload };
}
